import React from 'react';
import * as Icons from 'lucide-react';
import type { ReportDefinition } from '../../types/report.types';

interface ReportCardProps {
  definition: ReportDefinition;
  onGenerate: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

export function ReportCard({
  definition,
  onGenerate,
  disabled = false,
  disabledReason,
}: ReportCardProps): React.ReactElement {
  const iconMap = Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>;
  const Icon = iconMap[definition.icon] ?? Icons.FileText;

  return (
    <article className="rounded-xl border border-gray-100 bg-white p-6 transition-shadow hover:shadow-lg">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
          <Icon className="h-6 w-6" aria-hidden />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{definition.title}</h3>
          <p className="mt-1 text-sm text-gray-600">{definition.description}</p>
        </div>
      </div>

      <p className="mb-2 text-sm font-medium text-gray-700">Includes:</p>
      <ul className="mb-4 space-y-1">
        {definition.features.map((feature) => (
          <li key={feature} className="flex items-center gap-2 text-sm text-gray-600">
            <Icons.CheckCircle className="h-4 w-4 shrink-0 text-green-500" aria-hidden />
            {feature}
          </li>
        ))}
      </ul>

      {disabled && disabledReason && (
        <p className="mb-3 text-sm text-amber-700">{disabledReason}</p>
      )}
      <button
        type="button"
        onClick={onGenerate}
        disabled={disabled}
        className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
      >
        {disabled ? 'Not available in your region' : 'Generate Report'}
      </button>
    </article>
  );
}
